import React, { useState } from 'react';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import { SubmitButton } from '@/components/ui/Buttons';
import AIBadge from '@/components/shared/AIBadge';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import I18n from '@/modules/i18n';
import Axios from 'axios';
import * as Routes from 'modules/routes';
import DisableAiFeaturesModal from './DisableAiFeaturesModal';
import { TCI_AI_APPROACH_URL } from './aiFeatures.constants';
import SettingsToggle from './SettingsToggle';
import TurnOnAiFeaturesModal from './TurnOnAiFeaturesModal';
import ViewOnlyBanner from './ViewOnlyBanner';

interface Subscriber {
  ai_grading_enabled: boolean;
  ai_question_builder_enabled: boolean;
  ai_reading_levels_enabled: boolean;
  id: number;
}

interface AIFeaturesSettingsProps {
  active: boolean;
  canToggleGrading: boolean;
  canToggleQuestionBuilder: boolean;
  canToggleReadingLevels: boolean;
  isSysadmin?: boolean;
  readOnly?: boolean;
  subscriber: Subscriber;
}

interface ToggleState {
  ai_grading_enabled: boolean;
  ai_question_builder_enabled: boolean;
  ai_reading_levels_enabled: boolean;
}

// AIDEV-NOTE: Each toggle is gated by its own LaunchDarkly flag (see canToggle* props). A feature only
// appears when its flag is on for the subscriber; the AI Features tab itself shows if either flag is on.
const FEATURES = [
  {
    descriptionKey: 'ai_reading_levels_description',
    key: 'ai_reading_levels_enabled' as const,
    labelKey: 'ai_reading_levels',
    optOutBulletKeys: ['disable_ai_reading_levels_bullet', 'restore_level_b_bullet'],
  },
  {
    descriptionKey: 'ai_question_builder_description',
    key: 'ai_question_builder_enabled' as const,
    labelKey: 'ai_question_builder',
    optOutBulletKeys: ['disable_ai_feature_bullet'],
  },
  {
    descriptionKey: 'ai_grading_description',
    key: 'ai_grading_enabled' as const,
    labelKey: 'ai_grading',
    optOutBulletKeys: ['disable_ai_feature_bullet'],
  },
];

const AIFeaturesSettings = ({
  active,
  canToggleGrading,
  canToggleQuestionBuilder,
  canToggleReadingLevels,
  isSysadmin = false,
  readOnly = false,
  subscriber,
}: AIFeaturesSettingsProps) => {
  const initialState: ToggleState = {
    ai_grading_enabled: subscriber.ai_grading_enabled,
    ai_question_builder_enabled: subscriber.ai_question_builder_enabled,
    ai_reading_levels_enabled: subscriber.ai_reading_levels_enabled,
  };

  const [toggles, setToggles] = useState<ToggleState>(initialState);
  const [lastSaved, setLastSaved] = useState<ToggleState>(initialState);
  const [showTurnOnModal, setShowTurnOnModal] = useState(false);
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const visibleFeatureKeys = new Set<keyof ToggleState>();
  if (isSysadmin || canToggleReadingLevels) visibleFeatureKeys.add('ai_reading_levels_enabled');
  if (isSysadmin || canToggleQuestionBuilder) visibleFeatureKeys.add('ai_question_builder_enabled');
  if (isSysadmin || canToggleGrading) visibleFeatureKeys.add('ai_grading_enabled');
  const visibleFeatures = FEATURES.filter(f => visibleFeatureKeys.has(f.key));

  const isPristine = (Object.keys(toggles) as Array<keyof ToggleState>).every(k => toggles[k] === lastSaved[k]);

  const enabledFeatures = visibleFeatures.filter(
    f => !lastSaved[f.key] && toggles[f.key],
  );

  const disabledFeatures = visibleFeatures.filter(
    f => lastSaved[f.key] && !toggles[f.key],
  );

  const confirmedDisabledFeatures = disabledFeatures.filter(f => f.optOutBulletKeys.length > 0);

  const { isError, isLoading, mutate: saveSettings } = useMutation({
    mutationFn: (newToggles: ToggleState) => Axios.patch(Routes.plato_api_subscriber_path(subscriber.id), {
      subscriber: Object.fromEntries(visibleFeatures.map(f => [f.key, newToggles[f.key]])),
    }),
    onError: () => {
      setToggles(lastSaved);
    },
    onSuccess: (_data, variables) => {
      setLastSaved(variables);
    },
  });

  // AIDEV-NOTE: A mixed on+off Update shows only the turn-on modal — authorization is the higher bar,
  // and one dialog beats chaining two. The opt-out modal is for turn-off-only Updates.
  const handleUpdate = () => {
    if (enabledFeatures.length > 0) {
      setShowTurnOnModal(true);
      return;
    }

    if (confirmedDisabledFeatures.length > 0) {
      setShowOptOutModal(true);
      return;
    }

    saveSettings(toggles);
  };

  const handleConfirmTurnOn = () => {
    setShowTurnOnModal(false);
    saveSettings(toggles);
  };

  const handleConfirmOptOut = () => {
    setShowOptOutModal(false);
    saveSettings(toggles);
  };

  const optOutBulletKeys = disabledFeatures.length === visibleFeatures.length
    ? ['disable_ai_features_all_bullet']
    : [...new Set(disabledFeatures.flatMap(f => f.optOutBulletKeys))];

  const handleToggle = (key: keyof ToggleState, checked: boolean) => {
    setToggles(prev => ({ ...prev, [key]: checked }));
  };

  if (!active) return null;

  return (
    <div>
      {readOnly && !bannerDismissed && (
        <ViewOnlyBanner onDismiss={() => setBannerDismissed(true)} />
      )}

      <div className="tw-mb-6">
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <Text size="xl" tag="h2" variant="label">{I18n.t('ai_features')}</Text>
          <AIBadge />
        </div>
        <Text italic className="!tw-text-very-dark-grey" size="sm">
          {I18n.t('ai_features_description')}
        </Text>
      </div>

      <div className="tw-space-y-6">
        {visibleFeatures.map(feature => (
          <SettingsToggle
            key={feature.key}
            checked={toggles[feature.key]}
            description={I18n.t(feature.descriptionKey)}
            disabled={readOnly}
            id={`ai-feature-${feature.key}`}
            label={I18n.t(feature.labelKey)}
            onChange={(checked: boolean) => handleToggle(feature.key, checked)}
          />
        ))}
      </div>

      <div className="tw-mt-6">
        <a
          className="tw-text-sm tw-inline-flex tw-items-center tw-gap-1"
          href={TCI_AI_APPROACH_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          {I18n.t('view_tci_approach_to_ai')}
          <Icon icon="ExternalLink" size={14} />
        </a>
      </div>

      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-2">
        {isError && (
          <span className="tw-text-red-600">
            {I18n.t('ai_features_update_error')}
          </span>
        )}
        <SubmitButton
          disabled={isPristine || readOnly}
          submitting={isLoading}
          onClick={handleUpdate}
        >
          {I18n.t('update_button')}
        </SubmitButton>
      </div>

      {showTurnOnModal && (
        <TurnOnAiFeaturesModal
          onCancel={() => setShowTurnOnModal(false)}
          onConfirm={handleConfirmTurnOn}
        />
      )}

      {showOptOutModal && (
        <DisableAiFeaturesModal
          bulletKeys={optOutBulletKeys}
          onCancel={() => setShowOptOutModal(false)}
          onConfirm={handleConfirmOptOut}
        />
      )}
    </div>
  );
};

const AIFeaturesSettingsWrapper = (props: AIFeaturesSettingsProps) => (
  <ReactQueryProvider>
    <AIFeaturesSettings {...props} />
  </ReactQueryProvider>
);

export default AIFeaturesSettingsWrapper;
